import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { departmentService, employmentService } from '../../services/api';
import { apiErrorText, CHECK_COLORS, fmtDate, fmtMoney, isoToday } from './salaryFormat';

// Gross and net in separate columns; the value written in the document is bold, the other is calculated.
function MoneyCell({ salary, basis }) {
  if (!salary) return <TableCell align="right">—</TableCell>;
  const fromDocument = salary.basis === basis;
  return (
    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: fromDocument ? 600 : 400, whiteSpace: 'nowrap' }}>
      {fmtMoney(basis === 'Gross' ? salary.gross : salary.net, salary.currency)}
    </TableCell>
  );
}

// S3 — Salary Register: the contracted salary of every employee on a date, its source document,
// the next scheduled change and checks. Visible only with the ViewContracts permission.
export default function SalaryRegisterPage() {
  const [filters, setFilters] = React.useState({ asOf: isoToday(), departmentId: '', status: 'current', search: '' });
  const [rows, setRows] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const params = React.useMemo(() => ({
    asOf: filters.asOf || undefined,
    departmentId: filters.departmentId || undefined,
    status: filters.status,
    search: filters.search || undefined,
  }), [filters]);

  React.useEffect(() => {
    departmentService.getAll().then((d) => setDepartments(Array.isArray(d) ? d : [])).catch(() => setDepartments([]));
  }, []);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const t = setTimeout(() => {
      employmentService.getRegister(params)
        .then((r) => { if (active) setRows(Array.isArray(r) ? r : []); })
        .catch((err) => {
          if (!active) return;
          setRows([]);
          setError(err?.response?.status === 403 ? 'You do not have permission to view salaries.' : apiErrorText(err, 'Could not load the register.'));
        })
        .finally(() => { if (active) setLoading(false); });
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [params]);

  const exportExcel = async () => {
    try {
      const blob = await employmentService.exportRegister(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SalaryRegister_${filters.asOf || isoToday()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(apiErrorText(err, 'Could not export the register.'));
    }
  };

  const set = (name) => (e) => setFilters((f) => ({ ...f, [name]: e.target.value }));

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h6" sx={{ flex: 1 }}>Salary Register</Typography>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={!!error || loading}>Export to Excel</Button>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField id="sr-asof" type="date" label="As of" InputLabelProps={{ shrink: true }} value={filters.asOf} onChange={set('asOf')} />
        <TextField id="sr-dept" select label="Department" value={filters.departmentId} onChange={set('departmentId')} sx={{ minWidth: 200 }}>
          <MenuItem value="">All</MenuItem>
          {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
        </TextField>
        <TextField id="sr-status" select label="Status" value={filters.status} onChange={set('status')} sx={{ minWidth: 180 }}>
          <MenuItem value="current">Active + leaving</MenuItem>
          <MenuItem value="terminated">Terminated</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </TextField>
        <TextField id="sr-search" label="Search" placeholder="name, position…" value={filters.search} onChange={set('search')} />
      </Stack>

      {error && <Alert severity={error.startsWith('You do not') ? 'info' : 'error'} sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : !error && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell><TableCell>Position</TableCell><TableCell>Department</TableCell>
                <TableCell align="right">Gross</TableCell><TableCell align="right">Net</TableCell><TableCell>In document</TableCell><TableCell>Since</TableCell>
                <TableCell>Source document</TableCell><TableCell>Next change</TableCell><TableCell>Checks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const s = r.currentSalary?.salary;
                const n = r.scheduledChange;
                return (
                  <TableRow key={`${r.personId}-${r.employeeId}`} hover>
                    <TableCell>{r.fullName}</TableCell>
                    <TableCell>{r.position || '—'}</TableCell>
                    <TableCell>{r.department || '—'}</TableCell>
                    <MoneyCell salary={s} basis="Gross" />
                    <MoneyCell salary={s} basis="Net" />
                    <TableCell>{s?.basis || '—'}</TableCell>
                    <TableCell>{fmtDate(r.currentSalary?.since)}</TableCell>
                    <TableCell>{r.currentSalary?.source || '—'}</TableCell>
                    <TableCell>
                      {n?.salary ? <>{fmtMoney(n.salary.gross, n.salary.currency)} gross / {fmtMoney(n.salary.net, n.salary.currency)} net · {fmtDate(n.effectiveDate)}{' '}
                        <Chip size="small" label={n.status} color={n.status === 'Draft' ? 'warning' : 'success'} /></> : '—'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {r.checks.length === 0
                          ? <Chip size="small" label="OK" color="success" variant="outlined" />
                          : r.checks.map((c) => <Chip key={c.code + c.text} size="small" label={c.text} color={CHECK_COLORS[c.code] || 'default'} />)}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={10}><Typography color="text.secondary">No employees match the filters.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        The amount written in the contract or the latest signed annex is in bold; the other one is calculated (base salary only).
        Visible only to people allowed to view contracts.
      </Typography>
    </Paper>
  );
}
